import type { CompanyPaceSummary } from "@/company/core/types";
import { formatMetricForCell } from "@/grid/core/format-metric";
import { TemperatureGauge, gaugeStatusColor } from "@/company/components/TemperatureGauge";
import { cn } from "@/lib/cn";

interface PaceGaugeProps {
  summary: CompanyPaceSummary;
  className?: string;
  /** "tv" = Company Sales TV display (read from across the room). */
  variant?: "default" | "hero" | "tv";
  showTitle?: boolean;
}

const VARIANT = {
  default: {
    wrapper: "",
    header: "mb-4",
    title: "text-sm",
    percentage: "text-3xl",
    gaugeWidth: "max-w-xl",
    gaugeSize: "md",
    footer: "mt-5 text-sm",
  },
  hero: {
    wrapper: "py-2",
    header: "mb-4",
    title: "text-lg",
    percentage: "text-5xl sm:text-6xl",
    gaugeWidth: "max-w-xl",
    gaugeSize: "lg",
    footer: "mt-5 text-base",
  },
  tv: {
    wrapper: "",
    header: "mb-7",
    title: "text-2xl",
    percentage: "text-9xl",
    gaugeWidth: "max-w-3xl",
    gaugeSize: "xl",
    footer: "mt-8 text-3xl",
  },
} as const;

export function PaceGauge({
  summary,
  className,
  variant = "hero",
  showTitle = true,
}: PaceGaugeProps) {
  const styles = VARIANT[variant];

  return (
    <div className={cn("w-full", styles.wrapper, className)}>
      <div className={cn("flex flex-col items-center gap-2 text-center", styles.header)}>
        {showTitle ? (
          <p className={cn("font-semibold text-[var(--color-text)]", styles.title)}>
            {summary.label}
          </p>
        ) : null}
        <p
          className={cn(
            "font-bold tabular-nums leading-none",
            gaugeStatusColor(summary.percentage),
            styles.percentage,
          )}
        >
          {summary.percentage}%
        </p>
      </div>

      <div className={cn("mx-auto", styles.gaugeWidth)}>
        <TemperatureGauge percentage={summary.percentage} size={styles.gaugeSize} />
      </div>

      <div className={cn("text-center", styles.footer)}>
        <p className="font-semibold tabular-nums text-[var(--color-text)]">
          {formatMetricForCell(summary.actual, "currency")}
          <span className="font-normal text-[var(--color-text-muted)]"> actual</span>
        </p>
        <p className="mt-1 tabular-nums text-[var(--color-text-muted)]">
          vs {formatMetricForCell(summary.compareTarget, "currency")}
          {summary.targetSuffix || " goal"}
        </p>
      </div>
    </div>
  );
}
